/**
 * `<WorkspaceRightPanel>` — floating right-side panel for the new UI.
 *
 * Two sections:
 *   1. Progress — 7-phase checklist
 *   2. Agents   — live agent activity
 *
 * Design principles:
 *   - No bold or medium weight — hierarchy comes from spacing and color
 *   - Generous vertical rhythm — rows breathe, nothing is compressed
 *   - Thin, delicate indicators — dots and lines, not heavy badges
 */
import * as React from 'react';
import { Check, AlertTriangle, X, Pencil } from 'lucide-react';
import type { ProgressPhaseNumber } from '@volley/crucible-ipc-types/checkpoint-content';
import type {
  PhaseProgressUpdate,
  ActivitySnapshot,
  AgentActivity,
  DerivedPhaseWire,
} from '@volley/crucible-ipc-types/ipc-channels';
import type {
  BuildGameInterviewState,
  BuildGameInterviewStatusSummary,
} from '@volley/crucible-ipc-types/build-game-types';
import {
  getEffectiveInterviewDepth,
  getEffectiveInterviewSubPhase,
} from '@volley/crucible-ipc-types/build-game-types';
import {
  PHASE_LABELS,
  chipStatesFromDerived,
  phaseLabelFromPhaseNumber,
  phaseStartedAtMs,
  type PhaseChipState,
} from './phase-utils';
import { useElapsedTick } from '../hooks/useElapsedTick';
import type { ActivityLiveness } from '../chat/activity-freshness';
import { formatElapsedMs } from './ElapsedTickSpan';
import { isMachineSubPhaseId, knownSubPhaseLabel } from '../workspace/subPhaseLabels';
import {
  isCompletionVerificationRetryRunState,
  type WorkspaceRunState,
  type WorkspaceRunTone,
} from '../workspace/run-state';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkspaceRightPanelProps {
  open: boolean;
  onClose: () => void;
  phaseProgress: PhaseProgressUpdate | undefined;
  activitySnapshot: ActivitySnapshot | null;
  runState?: WorkspaceRunState | null;
  interviewState?: BuildGameInterviewState | null;
  interviewSummary?: BuildGameInterviewStatusSummary | null;
  /**
   * The SINGLE build-watch liveness verdict from `useActivityLiveness`
   * (#3033/#3034), computed once at the Workspace level and passed down. Drives
   * the now-summary's poll-fed build liveness so it reads "Working"/"Build
   * running"/"No recent activity" from the ONE client-relative source instead
   * of recomputing against its own clock. Defaults to `unknown`.
   */
  buildLiveness?: ActivityLiveness;
  onEditDiscovery?: () => void;
  onResumeBuild?: () => void;
  /**
   * Layout mode.
   *   - `'floating'` (default): the slide-in overlay used outside a
   *     build — `absolute`, z-40, animates in/out via `open`.
   *   - `'pinned'`: an in-flow column that always occupies its width.
   *     Used by the three-zone build view, where the panel is a
   *     permanent right rail beside the embedded preview (no slide
   *     animation, `open` is ignored). They no longer compete for
   *     space because the build layout reserves the column.
   */
  variant?: 'floating' | 'pinned';
}

// ---------------------------------------------------------------------------
// Panel root
// ---------------------------------------------------------------------------

export function WorkspaceRightPanel({
  open,
  phaseProgress,
  activitySnapshot,
  runState,
  interviewState,
  interviewSummary,
  buildLiveness = 'unknown',
  onEditDiscovery,
  onResumeBuild,
  variant = 'floating',
}: WorkspaceRightPanelProps): React.JSX.Element {
  const pinned = variant === 'pinned';

  const progress = (
    <ProgressSection
      phaseProgress={phaseProgress}
      activitySnapshot={activitySnapshot}
      runState={runState}
      interviewState={interviewState}
      interviewSummary={interviewSummary}
      buildLiveness={buildLiveness}
      onEditDiscovery={onEditDiscovery}
      onResumeBuild={onResumeBuild}
    />
  );
  const agents = <AgentsSection snapshot={activitySnapshot} runState={runState} />;

  // Pinned (the chat panel's "State" tab): NO panel chrome — the tab is
  // already a framed surface, so a card-in-a-panel reads as a double border.
  // Render the content flat, filling the tab width, as one scroll column with
  // Progress then Agents stacked.
  if (pinned) {
    return (
      <div
        data-testid="workspace-right-panel"
        className="flex h-full w-full min-w-0 flex-col overflow-y-auto"
      >
        {progress}
        <Divider />
        {agents}
      </div>
    );
  }

  // Floating overlay (used outside a build): keep the rounded card chrome and
  // the two independently-scrolling regions so the live-agents section stays
  // visible even when a long Progress checklist would otherwise push it off
  // the bottom.
  return (
    <div
      aria-hidden={!open}
      data-testid="workspace-right-panel"
      className="pointer-events-none absolute bottom-0 right-0 top-0 z-40 flex w-96 flex-col"
      style={{
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: `transform var(--motion-med) var(--ease-flora)`,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <div
        className="m-3 flex flex-1 flex-col overflow-hidden rounded-2xl"
        style={{
          background: 'var(--surface-card)',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--border-alpha-1)',
        }}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">{progress}</div>
          <Divider />
          <div className="min-h-0 flex-1 overflow-y-auto">{agents}</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <p
      className="mb-5 text-[10px] uppercase tracking-[0.14em]"
      style={{ color: 'var(--text-3)', fontWeight: 400 }}
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Progress section
// ---------------------------------------------------------------------------

function ProgressSection({
  phaseProgress,
  activitySnapshot,
  runState,
  interviewState,
  interviewSummary,
  buildLiveness = 'unknown',
  onEditDiscovery,
  onResumeBuild,
}: {
  phaseProgress: PhaseProgressUpdate | undefined;
  activitySnapshot: ActivitySnapshot | null;
  runState?: WorkspaceRunState | null;
  interviewState?: BuildGameInterviewState | null;
  interviewSummary?: BuildGameInterviewStatusSummary | null;
  buildLiveness?: ActivityLiveness;
  onEditDiscovery?: () => void;
  onResumeBuild?: () => void;
}): React.JSX.Element {
  // #3034 — no per-consumer heartbeat here anymore: `buildLiveness` is the ONE
  // client-relative verdict from `useActivityLiveness`, whose single clock
  // re-renders Workspace (and so this panel via the prop) as wall-clock advances
  // even with NO new poll data. The now-summary just branches on it.
  const viewModel = deriveWorkspaceProgressViewModel({
    interviewState,
    interviewSummary,
    runState,
    phaseProgress,
    activitySnapshot,
    buildLiveness,
  });

  let errorNote: string | null = null;
  if (runState?.kind === 'done') {
    // A done build suppresses any progress-read error note (errorNote
    // stays null); the condition short-circuits the error branches below.
  } else if (phaseProgress?.kind === 'read-error') {
    errorNote = `Couldn't read build-progress.json (${phaseProgress.code})`;
  } else if (phaseProgress?.kind === 'parse-error') {
    errorNote = 'build-progress.json has a parse error — progress may be stale';
  }

  return (
    <section className="px-5 py-6">
      <SectionHeader>Progress</SectionHeader>

      {errorNote && (
        <p
          className="mb-4 rounded-lg px-3 py-2.5 text-[11px] leading-relaxed"
          style={{
            background: 'rgba(251,191,36,0.08)',
            color: 'rgb(252,211,77,0.8)',
            border: '1px solid rgba(251,191,36,0.15)',
          }}
        >
          {errorNote}
        </p>
      )}

      <NowSummary summary={viewModel.now} onPrimaryAction={onResumeBuild} />

      <ol>
        {viewModel.interviewRow && (
          <InterviewRow row={viewModel.interviewRow} onEdit={onEditDiscovery} />
        )}
        {[viewModel.planRow, viewModel.approvePlanRow, ...viewModel.phaseRows].map((row) => (
          <ProgressRow
            key={row.phaseNumber}
            phaseNumber={row.phaseNumber}
            label={row.label}
            state={row.state}
            progress={row.progress}
            phaseStartedAt={row.phaseStartedAt}
          />
        ))}
      </ol>
    </section>
  );
}

export interface NowSummaryModel {
  stateLabel: string;
  phaseLabel: string;
  checkpoint: string | null;
  progressLabel: string | null;
  tone: 'default' | 'waiting' | 'error' | 'done';
  primaryActionLabel?: string;
}

export interface WorkspaceProgressInterviewRowModel {
  label: 'Interview';
  state: PhaseChipState;
  answeredCount?: number;
  totalCount?: number;
  showCount: boolean;
  canEdit: boolean;
}

export interface WorkspaceProgressPhaseRowModel {
  phaseNumber: ProgressPhaseNumber;
  label: string;
  state: PhaseChipState;
  progress?: { completed: number; total: number };
  phaseStartedAt?: number;
}

export interface WorkspaceProgressViewModel {
  now: NowSummaryModel;
  interviewRow: WorkspaceProgressInterviewRowModel | null;
  planRow: WorkspaceProgressPhaseRowModel;
  approvePlanRow: WorkspaceProgressPhaseRowModel;
  phaseRows: readonly WorkspaceProgressPhaseRowModel[];
  timerAnchors: Readonly<Record<string, number>>;
}

export function deriveWorkspaceProgressViewModel({
  interviewState,
  interviewSummary,
  runState,
  phaseProgress,
  activitySnapshot,
  buildLiveness = 'unknown',
}: {
  interviewState?: BuildGameInterviewState | null;
  interviewSummary?: BuildGameInterviewStatusSummary | null;
  runState?: WorkspaceRunState | null;
  phaseProgress: PhaseProgressUpdate | undefined;
  activitySnapshot: ActivitySnapshot | null;
  /** The SINGLE build-watch liveness verdict (#3033/#3034). Defaults `unknown`. */
  buildLiveness?: ActivityLiveness;
}): WorkspaceProgressViewModel {
  const derived = phaseProgress?.kind === 'derived' ? phaseProgress.derived : null;
  const displayCurrent = displayPhaseNumber(derived, activitySnapshot);
  const states = phaseStatesForDisplay(derived, displayCurrent, runState);
  const phaseRows = PHASE_LABELS.map((entry, idx): WorkspaceProgressPhaseRowModel => {
    const state = states[idx] ?? 'pending';
    const isCurrent = state === 'in-progress' || state === 'blocked';
    const isDurableCurrent = derived !== null && entry.phase === derived.current;
    const phaseStartedAt =
      isCurrent && isDurableCurrent
        ? (phaseStartedAtMs(derived.transitions, entry.phase) ?? undefined)
        : undefined;
    return {
      phaseNumber: entry.phase,
      label: entry.short,
      state,
      progress: isCurrent && isDurableCurrent ? (derived.progress ?? undefined) : undefined,
      phaseStartedAt,
    };
  });
  const timerAnchors: Record<string, number> = {};
  for (const row of phaseRows) {
    if (row.phaseStartedAt !== undefined) {
      timerAnchors[`phase-${row.phaseNumber}`] = row.phaseStartedAt;
    }
  }
  return {
    now: deriveNowSummary(phaseProgress, activitySnapshot, runState, buildLiveness),
    interviewRow: deriveInterviewRowModel(interviewState, interviewSummary, runState),
    planRow: phaseRows[0] ?? fallbackPhaseRow(1),
    approvePlanRow: phaseRows[1] ?? fallbackPhaseRow(2),
    phaseRows: phaseRows.slice(2),
    timerAnchors,
  };
}

function fallbackPhaseRow(phaseNumber: ProgressPhaseNumber): WorkspaceProgressPhaseRowModel {
  const entry = PHASE_LABELS[phaseNumber - 1];
  return {
    phaseNumber,
    label: entry?.short ?? phaseLabelFromPhaseNumber(phaseNumber) ?? `Phase ${phaseNumber}`,
    state: 'pending',
  };
}

function deriveInterviewRowModel(
  state: BuildGameInterviewState | null | undefined,
  summary: BuildGameInterviewStatusSummary | null | undefined,
  runState?: WorkspaceRunState | null,
): WorkspaceProgressInterviewRowModel | null {
  if (!state && !summary && !runState) return null;
  const fullCounts = countInterviewQuestionsFromState(state);
  const summaryCounts =
    typeof summary?.answeredCount === 'number' && typeof summary?.totalCount === 'number'
      ? { answeredCount: summary.answeredCount, totalCount: summary.totalCount }
      : null;
  const counts = fullCounts ?? summaryCounts;
  const summaryStatus = summary?.status ?? null;
  const pastInterview = isPastInterview(state, runState, summaryStatus);
  const done =
    pastInterview ||
    summaryStatus === 'dispatched' ||
    summaryStatus === 'building' ||
    summaryStatus === 'built' ||
    summaryStatus === 'built-partial';
  const active =
    !done &&
    (summaryStatus === 'in-progress' ||
      summaryStatus === 'complete' ||
      runState?.kind === 'starting-interview' ||
      runState?.kind === 'interview-in-progress');
  return {
    label: 'Interview',
    state: done ? 'done' : active ? 'in-progress' : 'pending',
    answeredCount: counts?.answeredCount,
    totalCount: counts?.totalCount,
    showCount: active && counts !== null && counts.totalCount > 0,
    canEdit: done || active,
  };
}

function isPastInterview(
  state: BuildGameInterviewState | null | undefined,
  runState: WorkspaceRunState | null | undefined,
  summaryStatus: BuildGameInterviewStatusSummary['status'] | null,
): boolean {
  const depth = state ? getEffectiveInterviewDepth(state) : undefined;
  const subPhase = state ? getEffectiveInterviewSubPhase(state) : undefined;
  const quickPastInterview =
    depth === 'quick' &&
    (subPhase === 'plan' ||
      subPhase === 'refine' ||
      subPhase === 'committed' ||
      subPhase === 'build' ||
      subPhase === 'back-to-plan');
  if (quickPastInterview) return true;
  if (runState?.legacyCompletionUnverified === true) return true;
  if (
    runState?.kind === 'drafting-plan' ||
    runState?.kind === 'waiting-for-plan-review' ||
    runState?.kind === 'build-kickoff-pending' ||
    runState?.kind === 'building' ||
    runState?.kind === 'done' ||
    runState?.kind === 'needs-attention'
  ) {
    return true;
  }
  return (
    summaryStatus === 'dispatched' ||
    summaryStatus === 'building' ||
    summaryStatus === 'built' ||
    summaryStatus === 'built-partial'
  );
}

function countInterviewQuestionsFromState(
  state: BuildGameInterviewState | null | undefined,
): { answeredCount: number; totalCount: number } | null {
  if (!state?.agents) return null;
  let answeredCount = 0;
  let totalCount = 0;
  for (const block of Object.values(state.agents)) {
    const questions = questionsFromUnknown((block as { questions?: unknown }).questions);
    for (const question of questions) {
      totalCount += 1;
      const answer = typeof question.answer === 'string' ? question.answer.trim() : '';
      if (answer.length > 0 || typeof question.skippedAt === 'string') {
        answeredCount += 1;
      }
    }
  }
  return totalCount > 0 ? { answeredCount, totalCount } : null;
}

function questionsFromUnknown(value: unknown): Array<{ answer?: unknown; skippedAt?: unknown }> {
  if (Array.isArray(value)) {
    return value.filter(isQuestionLike) as Array<{ answer?: unknown; skippedAt?: unknown }>;
  }
  if (value && typeof value === 'object') {
    return Object.values(value).filter(isQuestionLike) as Array<{
      answer?: unknown;
      skippedAt?: unknown;
    }>;
  }
  return [];
}

function isQuestionLike(value: unknown): value is { answer?: unknown; skippedAt?: unknown } {
  return value !== null && typeof value === 'object';
}

function deriveNowSummary(
  phaseProgress: PhaseProgressUpdate | undefined,
  activitySnapshot: ActivitySnapshot | null,
  runState?: WorkspaceRunState | null,
  liveness: ActivityLiveness = 'unknown',
): NowSummaryModel {
  const derived = phaseProgress?.kind === 'derived' ? phaseProgress.derived : null;
  const agents = activitySnapshot ? Object.values(activitySnapshot.byAgentId) : [];
  const selectedAgent = activitySnapshot
    ? activitySnapshot.byAgentId[activitySnapshot.currentSelection]
    : undefined;
  const checkpointAgent =
    selectedAgent ??
    agents.find((agent) => agent.status === 'running' && agent.action) ??
    agents.find((agent) => agent.status === 'error' && agent.action) ??
    agents.find((agent) => agent.action);
  const checkpoint =
    checkpointFromAgent(checkpointAgent) ?? checkpointFromSubPhase(derived?.subPhase) ?? null;
  const phaseLabel =
    phaseLabelFromPhaseNumber(displayPhaseNumber(derived, activitySnapshot)) ??
    phaseLabelFromSnapshot(activitySnapshot) ??
    (phaseProgress?.kind === 'parse-error' || phaseProgress?.kind === 'read-error'
      ? 'Progress unavailable'
      : 'Planning');
  const progressLabel = derived?.progress
    ? `${derived.progress.completed}/${derived.progress.total} checks`
    : null;
  const hasErrorAgent = agents.some((agent) => agent.status === 'error');
  const hasFreshRunningAgent = agents.some(
    (agent) => agent.status === 'running' && agent.stale !== true,
  );
  const hasRunningAgent = agents.some((agent) => agent.status === 'running');

  if (runState?.kind === 'done') {
    return nowSummaryFromRunState(runState, phaseLabel, checkpoint, progressLabel);
  }
  if (runState?.kind === 'building' && derived && !hasFreshRunningAgent) {
    if (derived.blockedBy || hasErrorAgent) {
      return {
        stateLabel: hasErrorAgent ? 'Error' : 'Blocked',
        phaseLabel,
        checkpoint,
        progressLabel,
        tone: 'error',
      };
    }
    if (isDerivedProgressComplete(derived)) {
      return nowSummaryFromRunState(runState, phaseLabel, checkpoint, progressLabel);
    }
    // A poll-fed cloud build carries no attached activity snapshot, so the
    // absence of a fresh agent ROW is not proof the build is idle. lastActivityAt
    // is that proof. Two-tier because the stamp is turn-gated (see
    // activity-freshness.ts): fresh ⇒ "Working"; quiet (between turns) or
    // unknown (no stamp — older pod / pre-turn) ⇒ neutral, never a false stall
    // (rider 1); stalled (quiet past the turn-boundary window) ⇒ an honest stall.
    // Retires the old "No live agents" sibling of the chat status row (#2347/#3025).
    // `liveness` is the ONE client-relative verdict from `useActivityLiveness`
    // (#3033/#3034), passed in rather than recomputed against a local clock.
    if (liveness === 'fresh') {
      return {
        stateLabel: 'Working',
        phaseLabel,
        checkpoint: checkpoint ?? runState.detail ?? null,
        progressLabel,
        tone: 'default',
      };
    }
    if (liveness === 'stalled') {
      return {
        stateLabel: 'No recent activity',
        phaseLabel,
        checkpoint: checkpoint ?? runState.detail ?? null,
        progressLabel,
        tone: 'waiting',
      };
    }
    // 'quiet' | 'unknown' — running with no fresh signal. Preserve the
    // pre-existing stale-agent-row nuance; for the no-row case report a neutral
    // "Build running" instead of the retired "No live agents".
    return hasRunningAgent
      ? {
          stateLabel: 'No recent updates',
          phaseLabel,
          checkpoint: checkpoint ?? runState.detail ?? null,
          progressLabel,
          tone: 'waiting',
        }
      : {
          stateLabel: 'Build running',
          phaseLabel,
          checkpoint: checkpoint ?? runState.detail ?? null,
          progressLabel,
          tone: 'waiting',
        };
  }
  if (phaseProgress?.kind === 'parse-error' || phaseProgress?.kind === 'read-error') {
    return {
      stateLabel: 'Needs attention',
      phaseLabel,
      checkpoint,
      progressLabel,
      tone: 'error',
    };
  }
  if (runState) {
    return nowSummaryFromRunState(runState, phaseLabel, checkpoint, progressLabel);
  }
  if (derived?.blockedBy || hasErrorAgent) {
    return {
      stateLabel: hasErrorAgent ? 'Error' : 'Blocked',
      phaseLabel,
      checkpoint,
      progressLabel,
      tone: 'error',
    };
  }
  if (activitySnapshot?.heartbeatState === 'done') {
    return {
      stateLabel: 'Done',
      phaseLabel,
      checkpoint,
      progressLabel,
      tone: 'done',
    };
  }
  if (activitySnapshot?.heartbeatState === 'waiting') {
    return {
      stateLabel: 'Waiting',
      phaseLabel,
      checkpoint,
      progressLabel,
      tone: 'waiting',
    };
  }
  if (
    activitySnapshot?.heartbeatState === 'active' ||
    activitySnapshot?.heartbeatState === 'silent-but-active'
  ) {
    return {
      stateLabel: 'Working',
      phaseLabel,
      checkpoint,
      progressLabel,
      tone: 'default',
    };
  }
  if (derived) {
    if (isDerivedProgressComplete(derived)) {
      return {
        stateLabel: 'Done',
        phaseLabel,
        checkpoint,
        progressLabel,
        tone: 'done',
      };
    }
    return {
      stateLabel: 'Waiting',
      phaseLabel,
      checkpoint,
      progressLabel,
      tone: 'waiting',
    };
  }
  return {
    stateLabel: 'Not started',
    phaseLabel,
    checkpoint,
    progressLabel,
    tone: 'default',
  };
}

/**
 * Build the Now summary from the durable workspace run state.
 *
 * Args:
 *   runState: Workspace lifecycle state from the kernel/interview model.
 *   phaseLabel: Fallback label from derived phase progress or activity.
 *   checkpoint: Fallback checkpoint from current activity.
 *   progressLabel: Checklist progress text from derived phase progress.
 *
 * Returns:
 *   Now summary with run-state label, detail, tone, and primary action.
 *
 * Raises:
 *   None.
 */
function nowSummaryFromRunState(
  runState: WorkspaceRunState,
  phaseLabel: string,
  checkpoint: string | null,
  progressLabel: string | null,
): NowSummaryModel {
  return {
    stateLabel: nowStateLabelFromRunState(runState),
    phaseLabel: nowPhaseLabelFromRunState(runState, phaseLabel),
    checkpoint: runState.detail ?? checkpoint,
    progressLabel,
    tone: nowToneFromRunState(runState.tone),
    primaryActionLabel: primaryActionLabelFromRunState(runState),
  };
}

function nowStateLabelFromRunState(runState: WorkspaceRunState): string {
  switch (runState.kind) {
    case 'ready':
      return runState.legacyCompletionUnverified === true ? runState.label : 'Not started';
    case 'waiting-for-plan-review':
    case 'waiting-on-user':
      return 'Waiting';
    case 'build-kickoff-pending':
      return 'Starting build';
    case 'done':
      return runState.label;
    case 'needs-attention':
      return 'Needs attention';
    default:
      return 'Working';
  }
}

function nowPhaseLabelFromRunState(
  runState: WorkspaceRunState,
  fallbackPhaseLabel: string,
): string {
  switch (runState.kind) {
    case 'starting-interview':
    case 'interview-in-progress':
      return 'Interview';
    case 'drafting-plan':
      return 'Planning';
    case 'waiting-for-plan-review':
      return 'Approve plan';
    case 'build-kickoff-pending':
      return runState.label;
    case 'building':
      return isPreBuildPhaseLabel(fallbackPhaseLabel) ? runState.label : fallbackPhaseLabel;
    case 'done':
    case 'needs-attention':
      return runState.label;
    default:
      return fallbackPhaseLabel;
  }
}

function isPreBuildPhaseLabel(label: string): boolean {
  return (
    label === 'Interview' || label === 'Plan' || label === 'Planning' || label === 'Approve plan'
  );
}

function nowToneFromRunState(tone: WorkspaceRunTone): NowSummaryModel['tone'] {
  switch (tone) {
    case 'error':
    case 'warning':
      return 'error';
    case 'waiting':
      return 'waiting';
    case 'success':
      return 'done';
    default:
      return 'default';
  }
}

function primaryActionLabelFromRunState(runState: WorkspaceRunState): string | undefined {
  if (
    runState.kind === 'needs-attention' &&
    runState.primaryCta === 'retry' &&
    (runState.debugSource.startsWith('kickoff:') ||
      runState.debugSource.startsWith('intake-authority:') ||
      runState.debugSource === 'interview-status:in-progress:stale')
  ) {
    return 'Retry interview';
  }
  if (isCompletionVerificationRetryRunState(runState)) {
    return 'Retry build';
  }
  if (
    runState.kind === 'needs-attention' &&
    runState.primaryCta === 'retry' &&
    (runState.debugSource.includes('watchdog') ||
      runState.debugSource.endsWith(':stale-final-review') ||
      runState.debugSource.endsWith(':stale-build'))
  ) {
    return 'Resume build';
  }
  if (
    runState.kind === 'needs-attention' &&
    runState.primaryCta === 'retry' &&
    runState.debugSource.startsWith('auto-build:')
  ) {
    return 'Retry setup';
  }
  return undefined;
}

function isDerivedProgressComplete(derived: DerivedPhaseWire): boolean {
  // Phase-local checklists can reach total before the build advances to phase 7.
  return derived.current >= 7;
}

function phaseLabelFromSnapshot(snapshot: ActivitySnapshot | null): string | null {
  return phaseLabelFromPhaseNumber(snapshot?.currentPhase);
}

function phaseStatesForDisplay(
  derived: DerivedPhaseWire | null,
  displayCurrent: ProgressPhaseNumber | null,
  runState?: WorkspaceRunState | null,
): readonly PhaseChipState[] {
  const durableStates: readonly PhaseChipState[] = derived
    ? chipStatesFromDerived(derived)
    : PHASE_LABELS.map(() => 'pending' as PhaseChipState);
  const displayStates =
    !displayCurrent || displayCurrent === derived?.current
      ? durableStates
      : PHASE_LABELS.map((entry, idx) => {
          if (entry.phase < displayCurrent) {
            return durableStates[idx] === 'done-partial' ? 'done-partial' : 'done';
          }
          if (entry.phase === displayCurrent) return 'in-progress';
          return 'pending';
        });
  return phaseStatesWithRunState(displayStates, runState);
}

function phaseStatesWithRunState(
  states: readonly PhaseChipState[],
  runState?: WorkspaceRunState | null,
): readonly PhaseChipState[] {
  if (!runState) return states;
  const next = [...states];
  const markDone = (phaseNumber: ProgressPhaseNumber): void => {
    const idx = phaseNumber - 1;
    if (next[idx] !== 'done' && next[idx] !== 'done-partial') next[idx] = 'done';
  };
  const markInProgressIfPending = (phaseNumber: ProgressPhaseNumber): void => {
    const idx = phaseNumber - 1;
    if (next[idx] === 'pending') next[idx] = 'in-progress';
  };

  if (runState.kind === 'waiting-for-plan-review') {
    markDone(1);
    markInProgressIfPending(2);
  } else if (runState.kind === 'build-kickoff-pending') {
    markDone(1);
    markDone(2);
  } else if (runState.kind === 'building') {
    markDone(1);
    markDone(2);
    const hasBuildPhaseState = next
      .slice(2)
      .some(
        (state) =>
          state === 'done' ||
          state === 'done-partial' ||
          state === 'in-progress' ||
          state === 'blocked',
      );
    if (!hasBuildPhaseState) markInProgressIfPending(3);
  } else if (runState.kind === 'done') {
    for (const entry of PHASE_LABELS) markDone(entry.phase);
  }

  return next;
}

function displayPhaseNumber(
  derived: DerivedPhaseWire | null,
  snapshot: ActivitySnapshot | null,
): ProgressPhaseNumber | null {
  const durable =
    asProgressPhaseNumber(derived?.current) ?? asProgressPhaseNumber(snapshot?.currentPhase);
  const live = livePhaseHintFromSnapshot(snapshot) ?? phaseNumberFromProgressCue(derived?.subPhase);
  if (live && (!durable || live > durable)) return live;
  return durable;
}

function livePhaseHintFromSnapshot(snapshot: ActivitySnapshot | null): ProgressPhaseNumber | null {
  if (!snapshot) return null;
  if (snapshot.heartbeatState === 'done') return null;
  const agents = Object.values(snapshot.byAgentId);
  const selected = snapshot.byAgentId[snapshot.currentSelection];
  const candidates = selected
    ? [selected, ...agents.filter((agent) => agent.agentId !== selected.agentId)]
    : agents;
  let highest: ProgressPhaseNumber | null = null;
  for (const agent of candidates) {
    for (const value of [agent.action, agent.subsystem]) {
      const phase = phaseNumberFromProgressCue(value);
      if (phase && (!highest || phase > highest)) highest = phase;
    }
  }
  return highest;
}

function asProgressPhaseNumber(value: number | null | undefined): ProgressPhaseNumber | null {
  return value === 1 ||
    value === 2 ||
    value === 3 ||
    value === 4 ||
    value === 5 ||
    value === 6 ||
    value === 7
    ? value
    : null;
}

function checkpointFromAgent(agent: AgentActivity | undefined): string | null {
  if (!agent?.action) return null;
  const action = agent.action.trim();
  if (action.length === 0) return null;
  const readableAction = humanReadableCheckpoint(action);
  if (!readableAction) return null;
  if (agent.subsystem && agent.agentId !== '__main__') {
    return `${agent.subsystem}: ${readableAction}`;
  }
  return readableAction;
}

function checkpointFromSubPhase(subPhase: string | undefined): string | null {
  if (!subPhase) return null;
  return humanReadableCheckpoint(subPhase);
}

function humanReadableCheckpoint(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const known = knownSubPhaseLabel(trimmed);
  if (known) return known;
  const machineCue = readableProgressCue(trimmed);
  if (machineCue) return machineCue;
  return isMachineSubPhaseId(trimmed) ? null : trimmed;
}

function phaseNumberFromProgressCue(value: string | null | undefined): ProgressPhaseNumber | null {
  if (!value) return null;
  const text = value.trim();
  if (text.length === 0) return null;
  if (/\bB\.7\.5\b/i.test(text)) return 7;
  if (/\bB\.7\b/i.test(text)) return 6;
  if (/\bB\.6\b/i.test(text)) return 6;
  if (/\bB\.(?:4c|5)\b/i.test(text)) return 5;
  if (/\bB\.(?:3|4b[-\s]+framework)\b/i.test(text)) return 3;
  if (/\bB\.4b(?:[-\s]+features)?\b/i.test(text)) return 4;
  if (/\bB\.(?:2(?:\.5)?|4a)\b/i.test(text)) return 2;
  if (/\bB\.1\b/i.test(text)) return 1;
  return null;
}

function readableProgressCue(value: string): string | null {
  const cases: ReadonlyArray<{
    pattern: RegExp;
    label: string;
  }> = [
    { pattern: /^B\.7\.5(?:[-\s:]+(.+))?$/i, label: 'Release handoff' },
    { pattern: /^B\.7(?:[-\s:]+(.+))?$/i, label: 'Pre-release prep' },
    { pattern: /^B\.6(?:[-\s:]+(.+))?$/i, label: 'Final review' },
    { pattern: /^B\.5(?:[-\s:]+(.+))?$/i, label: 'Build review' },
    { pattern: /^B\.4c(?:[-\s:]+(.+))?$/i, label: 'Post-feature integration' },
    { pattern: /^B\.4b[-\s]+framework(?:[-\s:]+(.+))?$/i, label: 'Framework scaffold' },
    { pattern: /^B\.4b[-\s]+features(?:[-\s:]+(.+))?$/i, label: 'Feature implementation' },
    { pattern: /^B\.4b(?:[-\s:]+(.+))?$/i, label: 'Feature implementation' },
    { pattern: /^B\.4a(?:[-\s:]+(.+))?$/i, label: 'Build plan review' },
    { pattern: /^B\.3(?:[-\s:]+(.+))?$/i, label: 'Pre-build setup' },
    { pattern: /^B\.2\.5(?:[-\s:]+(.+))?$/i, label: 'Plan review checkpoint' },
    { pattern: /^B\.2(?:[-\s:]+(.+))?$/i, label: 'Plan review prep' },
    { pattern: /^B\.1(?:[-\s:]+(.+))?$/i, label: 'Planning' },
  ];
  for (const entry of cases) {
    const match = value.match(entry.pattern);
    if (!match) continue;
    const detail = match[1]?.trim();
    return detail ? `${entry.label}: ${detail}` : entry.label;
  }
  return null;
}

function NowSummary({
  summary,
  onPrimaryAction,
}: {
  summary: NowSummaryModel;
  onPrimaryAction?: () => void;
}): React.JSX.Element {
  const toneColor =
    summary.tone === 'error'
      ? 'rgb(252,165,165)'
      : summary.tone === 'waiting'
        ? 'rgb(252,211,77)'
        : summary.tone === 'done'
          ? 'rgb(110,231,183)'
          : 'var(--text-2)';

  return (
    <div
      data-testid="workspace-right-panel-now-summary"
      className="mb-5 rounded-xl px-3 py-3"
      style={{
        background: 'rgba(255,255,255,0.035)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <span
          className="text-[10px] uppercase tracking-[0.12em]"
          style={{ color: 'var(--text-3)', fontWeight: 400 }}
        >
          Now
        </span>
        <span className="shrink-0 text-[11px]" style={{ color: toneColor, fontWeight: 400 }}>
          {summary.stateLabel}
        </span>
      </div>
      <p className="text-[13px] leading-snug" style={{ color: 'var(--text-1)', fontWeight: 400 }}>
        {summary.phaseLabel}
      </p>
      {summary.checkpoint && (
        <p
          className="mt-1 break-words text-[11px] leading-snug"
          style={{ color: 'var(--text-3)', fontWeight: 400 }}
          data-testid="workspace-right-panel-now-checkpoint"
        >
          {summary.checkpoint}
        </p>
      )}
      {summary.progressLabel && (
        <span
          className="mt-2 inline-flex text-[11px] tabular-nums"
          style={{ color: 'var(--text-3)', fontWeight: 400 }}
        >
          {summary.progressLabel}
        </span>
      )}
      {summary.primaryActionLabel && onPrimaryAction && (
        <button
          type="button"
          onClick={onPrimaryAction}
          className="mt-3 rounded-md border border-white/10 px-2.5 py-1.5 text-[11px] transition-colors hover:border-white/20 hover:bg-white/[0.05]"
          style={{ color: 'var(--text-1)', fontWeight: 400 }}
        >
          {summary.primaryActionLabel}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Interview row (pre-phase: game questions)
// ---------------------------------------------------------------------------

function InterviewRow({
  row,
  onEdit,
}: {
  row: WorkspaceProgressInterviewRowModel;
  onEdit?: () => void;
}): React.JSX.Element {
  const isDone = row.state === 'done' || row.state === 'done-partial';
  const isActive = row.state === 'in-progress' || row.state === 'blocked';

  const labelColor = isActive ? 'var(--text-1)' : isDone ? 'var(--text-3)' : 'var(--text-pending)';

  return (
    <li className="flex flex-col py-2.5">
      <div className="flex items-center gap-3">
        <ProgressDot state={row.state} />

        <span
          className={`flex-1 text-[13px] leading-snug${isDone ? ' line-through' : ''}`}
          style={{ color: labelColor, fontWeight: 400 }}
        >
          {row.label}
        </span>

        {/* answered / total count */}
        {row.showCount &&
          typeof row.answeredCount === 'number' &&
          typeof row.totalCount === 'number' && (
            <span
              className="flex-shrink-0 text-[11px] tabular-nums"
              style={{ color: 'var(--text-3)', fontWeight: 400 }}
            >
              {row.answeredCount}/{row.totalCount}
            </span>
          )}

        {/* edit answers button */}
        {row.canEdit && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            title="Edit answers"
            aria-label="Edit interview answers"
            className="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-white/[0.08]"
            style={{ color: 'var(--text-3)' }}
          >
            <Pencil size={10} strokeWidth={1.75} />
          </button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Progress row
// ---------------------------------------------------------------------------

interface ProgressRowProps {
  phaseNumber: number;
  label: string;
  state: PhaseChipState;
  progress?: { completed: number; total: number };
  phaseStartedAt?: number;
}

function ProgressRow({
  phaseNumber,
  label,
  state,
  progress,
  phaseStartedAt,
}: ProgressRowProps): React.JSX.Element {
  const isDone = state === 'done';
  const isPartial = state === 'done-partial';
  const isActive = state === 'in-progress';
  const isBlocked = state === 'blocked';
  const isPending = state === 'pending';

  const labelColor = isActive
    ? 'var(--text-1)'
    : isBlocked
      ? 'rgb(252,165,165)'
      : isDone || isPartial
        ? 'var(--text-3)'
        : isPending
          ? 'var(--text-pending)'
          : 'var(--text-2)';

  return (
    <li
      className="flex flex-col py-2.5"
      data-testid={`progress-phase-${phaseNumber}`}
      data-state={state}
    >
      <div className="flex items-center gap-3">
        <ProgressDot state={state} />

        <span
          className={`flex-1 text-[13px] leading-snug${isDone || isPartial ? ' line-through' : ''}`}
          style={{ color: labelColor, fontWeight: 400 }}
        >
          {label}
        </span>

        {/* Progress fraction for active phase */}
        {isActive && progress && (
          <span
            className="flex-shrink-0 text-[11px] tabular-nums"
            style={{ color: 'var(--text-3)', fontWeight: 400 }}
          >
            {progress.completed}/{progress.total}
          </span>
        )}
      </div>

      {/* Elapsed time */}
      {isActive && phaseStartedAt !== undefined && (
        <div className="mt-1 pl-8">
          <ElapsedSubRow phaseStartedAt={phaseStartedAt} />
        </div>
      )}

      {/* Blocked note */}
      {isBlocked && (
        <p
          className="mt-1 pl-8 text-[11px] leading-snug"
          style={{ color: 'rgba(248,113,113,0.6)', fontWeight: 400 }}
        >
          Waiting for direction
        </p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Progress dot
// ---------------------------------------------------------------------------

function ProgressDot({ state }: { state: PhaseChipState }): React.JSX.Element {
  const base = 'flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full';

  switch (state) {
    case 'done':
      return (
        <span
          className={base}
          style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)' }}
        >
          <Check size={10} strokeWidth={2} style={{ color: 'rgb(110,231,183)' }} />
        </span>
      );
    case 'done-partial':
      return (
        <span
          className={base}
          style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)' }}
        >
          <AlertTriangle size={9} strokeWidth={1.75} style={{ color: 'rgb(252,211,77)' }} />
        </span>
      );
    case 'in-progress':
      return (
        <span
          className={base}
          style={{ border: '1px solid rgba(124,111,247,0.5)', background: 'rgba(124,111,247,0.1)' }}
        >
          <span
            className="block h-1.5 w-1.5 animate-pulse rounded-full"
            style={{ background: 'var(--color-crucible-accent, #7C6FF7)' }}
          />
        </span>
      );
    case 'blocked':
      return (
        <span
          className={base}
          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)' }}
        >
          <X size={9} strokeWidth={1.75} style={{ color: 'rgb(252,165,165)' }} />
        </span>
      );
    case 'pending':
    default:
      return (
        <span
          className={base}
          style={{ border: '1px solid var(--border-pending)', background: 'transparent' }}
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Elapsed tick (isolated leaf so only this re-renders per second)
// ---------------------------------------------------------------------------

function ElapsedSubRow({ phaseStartedAt }: { phaseStartedAt: number }): React.JSX.Element {
  const tick = useElapsedTick(true);
  void tick;
  return (
    <span
      className="text-[11px] tabular-nums"
      style={{ color: 'var(--text-3)', fontWeight: 400 }}
      aria-hidden="true"
    >
      {formatElapsedMs(phaseStartedAt, null)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Agents section
// ---------------------------------------------------------------------------

function AgentsSection({
  snapshot,
  runState,
}: {
  snapshot: ActivitySnapshot | null;
  runState?: WorkspaceRunState | null;
}): React.JSX.Element {
  const agents = snapshot
    ? Object.values(snapshot.byAgentId).sort((a, b) => {
        if (a.agentId === '__main__') return -1;
        if (b.agentId === '__main__') return 1;
        return a.startedAt - b.startedAt;
      })
    : [];

  const liveCount = agents.filter((a) => a.status === 'running' && a.stale !== true).length;
  const staleRunningCount = agents.filter((a) => a.status === 'running' && a.stale === true).length;
  const emptyLabel =
    runState?.kind === 'starting-interview' ||
    runState?.kind === 'interview-in-progress' ||
    runState?.kind === 'drafting-plan' ||
    runState?.kind === 'waiting-for-plan-review' ||
    runState?.kind === 'build-kickoff-pending'
      ? 'No build agents yet'
      : 'No agents active';

  return (
    <section className="px-5 py-6">
      <div className="mb-5 flex items-baseline gap-2.5">
        <p
          className="text-[10px] uppercase tracking-[0.14em]"
          style={{ color: 'var(--text-3)', fontWeight: 400 }}
        >
          Agents
        </p>
        {liveCount > 0 && (
          <span
            className="flex items-center gap-1.5 text-[10px]"
            style={{ color: 'rgba(134,239,172,0.7)', fontWeight: 400 }}
          >
            <span className="h-1 w-1 animate-pulse rounded-full bg-green-400" />
            {liveCount} live
          </span>
        )}
        {staleRunningCount > 0 && (
          <span
            className="flex items-center gap-1.5 text-[10px]"
            style={{ color: 'rgba(251,191,36,0.7)', fontWeight: 400 }}
          >
            <span className="h-1 w-1 rounded-full bg-yellow-400" />
            {staleRunningCount} no update
          </span>
        )}
      </div>

      {agents.length === 0 ? (
        <p className="text-[13px]" style={{ color: 'var(--text-pending)', fontWeight: 400 }}>
          {emptyLabel}
        </p>
      ) : (
        <ul>
          {agents.map((agent) => (
            <AgentRow key={agent.agentId} agent={agent} />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Agent display name map
// Canonical agent types from ReviewerName + build-game specialists.
// Falls back to title-casing the slug for any unknown future types.
// ---------------------------------------------------------------------------

const AGENT_DISPLAY_NAMES: Record<string, string> = {
  'product-manager': 'Product Manager',
  'principal-engineer': 'Lead Programmer',
  'gameplay-engineer': 'Gameplay Programmer',
  'game-logic': 'Game Designer',
  'art-director': 'Art Director',
  'ui-implementor': 'UI Artist',
  '3d-graphics-engineer': '3D Artist',
  'pm-scope': 'Creative Director',
  'performance-auditor': 'Performance Engineer',
  'cross-app-consistency': 'Platform Engineer',
  'qa-engineer': 'QA Engineer',
};

// ---------------------------------------------------------------------------
// Agent row
// ---------------------------------------------------------------------------

function AgentRow({ agent }: { agent: AgentActivity }): React.JSX.Element {
  const isRunning = agent.status === 'running';
  const isError = agent.status === 'error';
  const isDone = agent.status === 'done';
  const isStale = isRunning && agent.stale === true;
  const isMain = agent.agentId === '__main__';

  const displayName = isMain
    ? 'Producer'
    : (AGENT_DISPLAY_NAMES[agent.agentType] ??
      agent.agentType.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));

  const dotColor = isError
    ? 'rgb(248,113,113)'
    : isStale
      ? 'rgb(251,191,36)'
      : isRunning
        ? 'rgb(74,222,128)'
        : 'rgba(255,255,255,0.12)';

  const nameColor = isError
    ? 'rgb(252,165,165)'
    : isStale
      ? 'rgb(252,211,77)'
      : isRunning
        ? 'var(--text-1)'
        : 'var(--text-3)';

  const statusLabel = isError ? 'error' : isStale ? 'No update 90s' : isRunning ? 'live' : null;
  const statusTitle = isStale ? 'Still running, but no recent activity update.' : undefined;

  const statusColor = isError
    ? 'rgba(248,113,113,0.6)'
    : isStale
      ? 'rgba(251,191,36,0.6)'
      : 'rgba(134,239,172,0.6)';

  const actionText = agent.action ? humanReadableCheckpoint(agent.action) : null;
  const subsystemText = agent.subsystem
    ? (humanReadableCheckpoint(agent.subsystem) ?? agent.subsystem)
    : null;
  const subText =
    subsystemText && !isMain
      ? `${subsystemText}${actionText ? ` — ${actionText}` : ''}`
      : actionText;

  return (
    <li className={`flex flex-col py-3${isDone ? ' opacity-40' : ''}`}>
      <div className="flex items-center gap-2.5">
        {/* Status dot */}
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
          <span
            className={`block h-1.5 w-1.5 rounded-full${isRunning && !isStale ? ' animate-pulse' : ''}`}
            style={{ background: dotColor }}
          />
        </span>

        {/* Name */}
        <span
          className="flex-1 text-[13px] leading-snug"
          style={{ color: nameColor, fontWeight: 400 }}
        >
          {displayName}
        </span>

        {/* Status label */}
        {statusLabel && (
          <span
            className="flex-shrink-0 text-[10px]"
            style={{ color: statusColor, fontWeight: 400 }}
            title={statusTitle}
          >
            {statusLabel}
          </span>
        )}
      </div>

      {/* Subsystem / action */}
      {subText && (
        <p
          className="mt-0.5 pl-[26px] text-[11px] leading-snug"
          style={{
            color: isError
              ? 'color-mix(in srgb, var(--color-error) 55%, transparent)'
              : 'var(--text-3)',
            fontWeight: 400,
          }}
        >
          {subText}
        </p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Divider
// ---------------------------------------------------------------------------

function Divider(): React.JSX.Element {
  return <div className="mx-5" style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />;
}
